import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { MenuIcon, SunIcon, MoonIcon, BellIcon, SearchIcon, UserIcon, LogOutIcon } from 'lucide-react';
import Breadcrumbs from './Breadcrumbs';
interface HeaderProps {
  toggleSidebar: () => void;
}
const Header: React.FC<HeaderProps> = ({
  toggleSidebar
}) => {
  const {
    theme,
    toggleTheme
  } = useTheme();
  const {
    user,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const notifications = [{
    id: 1,
    text: 'New merchant onboarding request',
    time: '5 min ago',
    read: false
  }, {
    id: 2,
    text: 'Document review pending for ABC Corp',
    time: '1 hour ago',
    read: false
  }, {
    id: 3,
    text: 'System maintenance scheduled',
    time: '3 hours ago',
    read: true
  }];
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle search logic here
    console.log('Searching for:', searchQuery);
  };
  return <header className={`border-b ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} px-4 py-3`} data-id="element-127">
      <div className="flex items-center justify-between" data-id="element-128">
        <div className="flex items-center space-x-4" data-id="element-129">
          <button onClick={toggleSidebar} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Toggle sidebar" data-id="element-130">
            <MenuIcon className="h-6 w-6" data-id="element-131" />
          </button>
          <Breadcrumbs data-id="element-132" />
        </div>
        <div className="hidden md:block flex-grow max-w-xl mx-4" data-id="element-133">
          <form onSubmit={handleSearch} className="relative" data-id="element-134">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-135">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-136" />
            </div>
            <input type="search" className={`block w-full p-2 pl-10 text-sm rounded-lg ${theme === 'dark' ? 'bg-gray-700 border-gray-600 placeholder-gray-400 text-white focus:border-blue-500' : 'bg-gray-50 border border-gray-300 text-gray-900 focus:border-blue-500'}`} placeholder="Search merchant, ID, CNIC, phone..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-137" />
          </form>
        </div>
        <div className="flex items-center space-x-4" data-id="element-138">
          <button onClick={toggleTheme} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700" aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-139">
            {theme === 'dark' ? <SunIcon className="h-6 w-6" data-id="element-140" /> : <MoonIcon className="h-6 w-6" data-id="element-141" />}
          </button>
          <div className="relative" data-id="element-142">
            <button onClick={() => {
            setShowNotifications(!showNotifications);
            setShowUserMenu(false);
          }} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 relative" aria-label="Notifications" data-id="element-143">
              <BellIcon className="h-6 w-6" data-id="element-144" />
              <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500" data-id="element-145"></span>
            </button>
            {showNotifications && <div className={`absolute right-0 mt-2 w-80 rounded-md shadow-lg ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`} data-id="element-146">
                <div className="py-2 px-4 border-b dark:border-gray-700 font-medium" data-id="element-147">
                  Notifications
                </div>
                <div className="max-h-96 overflow-y-auto" data-id="element-148">
                  {notifications.map(notification => <div key={notification.id} className={`px-4 py-3 border-b dark:border-gray-700 ${notification.read ? '' : 'bg-blue-50 dark:bg-blue-900/20'}`} data-id="element-149">
                      <p className="text-sm" data-id="element-150">{notification.text}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-151">
                        {notification.time}
                      </p>
                    </div>)}
                </div>
                <div className="py-2 px-4 text-center text-sm text-blue-600 dark:text-blue-400 hover:underline cursor-pointer" data-id="element-152">
                  View all notifications
                </div>
              </div>}
          </div>
          <div className="border-l dark:border-gray-700 pl-4 relative" data-id="element-153">
            <button className="flex items-center space-x-2" onClick={() => {
            setShowUserMenu(!showUserMenu);
            setShowNotifications(false);
          }} data-id="element-154">
              <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center" data-id="element-155">
                <UserIcon className="h-5 w-5" data-id="element-156" />
              </div>
              {user && <span className="hidden md:block text-sm font-medium" data-id="element-157">
                  {user.name}
                </span>}
            </button>
            {showUserMenu && <div className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`} data-id="element-158">
                <div className="py-1" data-id="element-159">
                  <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => {
                // Navigate to profile
                setShowUserMenu(false);
              }} data-id="element-160">
                    Your Profile
                  </button>
                  <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => {
                // Navigate to settings
                setShowUserMenu(false);
              }} data-id="element-161">
                    Settings
                  </button>
                  <hr className="my-1 border-gray-200 dark:border-gray-700" data-id="element-162" />
                  <button className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center" onClick={() => {
                logout();
                setShowUserMenu(false);
              }} data-id="element-163">
                    <LogOutIcon className="h-4 w-4 mr-2" data-id="element-164" />
                    Sign out
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </div>
      <div className="md:hidden mt-3" data-id="element-165">
        <form onSubmit={handleSearch} className="relative" data-id="element-166">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-167">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-168" />
          </div>
          <input type="search" className={`block w-full p-2 pl-10 text-sm rounded-lg ${theme === 'dark' ? 'bg-gray-700 border-gray-600 placeholder-gray-400 text-white focus:border-blue-500' : 'bg-gray-50 border border-gray-300 text-gray-900 focus:border-blue-500'}`} placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-169" />
        </form>
      </div>
    </header>;
};
export default Header;